// Task template data for reuse across the application

// Description Templates
export const descriptionTemplates = [
  { 
    id: 1,
    value: "bug", 
    label: "Bug Fix", 
    template: "Investigate and resolve [specific bug] in the [feature/module]. Steps to reproduce:\n1. [Step 1]\n2. [Step 2]\n3. [Step 3]\nExpected behavior: [describe]\nActual behavior: [describe]",
    departments: ["Product & Technology", "Data & Analytics"]
  },
  { 
    id: 2,
    value: "feature", 
    label: "New Feature", 
    template: "Implement a new [feature name] that enables users to [functionality]. The feature should include:\n- Integration with [existing system/module]\n- User interface enhancements for [specific user journey]\n- Performance optimization to maintain [benchmark metrics]\n- Security considerations for [data/user protection]",
    departments: ["Product & Technology", "Marketing & Growth", "Customer Success", "Operations"]
  },
  { 
    id: 3,
    value: "improvement", 
    label: "Improvement", 
    template: "Improve the existing [feature/module] by enhancing [specific aspects]. Current limitations include:\n- [Performance bottleneck/UX issue]\n- [Integration limitation with other systems]\n- [Scalability concerns]\nThe improvement should address these limitations while maintaining existing functionality and ensuring backward compatibility.",
    departments: ["Product & Technology", "Operations", "Data & Analytics"]
  },
  { 
    id: 4,
    value: "research", 
    label: "Research Task", 
    template: "Conduct research on [topic/technology] to determine its suitability for [business purpose]. The research should cover:\n- Market analysis and competitive landscape\n- Implementation costs and resource requirements\n- Potential ROI and business impact\n- Integration feasibility with our current tech stack\n- Compliance with our regulatory requirements",
    departments: ["Product & Technology", "Marketing & Growth", "Sales & Business Dev", "Data & Analytics", "Legal & Compliance"]
  },
  {
    id: 5,
    value: "documentation",
    label: "Documentation",
    template: "Create comprehensive documentation for [feature/system/process]. The documentation should include:\n- Executive summary for leadership\n- Detailed technical specifications for development team\n- User guides for customer-facing teams\n- Training materials for internal teams\n- Maintenance and troubleshooting procedures\n- Compliance and security considerations",
    departments: ["Product & Technology", "Operations", "Legal & Compliance"]
  },
  {
    id: 6,
    value: "marketing",
    label: "Marketing Campaign",
    template: "Develop a marketing campaign for [product/service/feature] targeting [audience segment]. The campaign should include:\n- Key messaging and unique selling propositions\n- Channel strategy (social, email, content, etc.)\n- Creative assets requirements\n- Budget allocation\n- Timeline with key milestones\n- Success metrics and KPIs",
    departments: ["Marketing & Growth", "Sales & Business Dev"]
  },
  {
    id: 7,
    value: "customer",
    label: "Customer Experience",
    template: "Enhance customer experience for [specific customer journey/touchpoint]. Areas to address:\n- Current friction points in [specific process]\n- Customer feedback analysis from [sources]\n- Proposed solutions for identified pain points\n- Success metrics (NPS, CSAT, retention metrics)\n- Implementation requirements",
    departments: ["Customer Success", "Marketing & Growth", "Operations"]
  },
  {
    id: 8,
    value: "analytics",
    label: "Data Analysis",
    template: "Analyze [data set/metrics] to provide insights on [business question]. The analysis should:\n- Identify key trends and patterns in [specific area]\n- Segment data by [relevant dimensions]\n- Provide actionable recommendations based on findings\n- Create visualizations for key stakeholders\n- Establish ongoing monitoring framework",
    departments: ["Data & Analytics", "Finance", "Marketing & Growth", "Sales & Business Dev"]
  },
  {
    id: 9,
    value: "compliance",
    label: "Compliance Update",
    template: "Update our [process/policy/system] to comply with [regulation/standard]. Required actions:\n- Gap analysis between current state and compliance requirements\n- Documentation updates needed\n- System or process changes required\n- Training plan for affected teams\n- Verification procedures to ensure compliance\n- Reporting mechanisms for ongoing compliance",
    departments: ["Legal & Compliance", "Finance", "Human Resources", "Operations"]
  },
  {
    id: 10,
    value: "financial",
    label: "Financial Process",
    template: "Optimize the [specific financial process] to improve [efficiency/accuracy/compliance]. The task involves:\n- Current process analysis and bottleneck identification\n- Benchmarking against industry best practices\n- Proposed changes with cost-benefit analysis\n- Implementation plan with timeline\n- Risk assessment and mitigation strategies",
    departments: ["Finance", "Operations", "Legal & Compliance"]
  },
  {
    id: 11,
    value: "hiring",
    label: "Recruitment Process",
    template: "Develop/improve recruitment process for [role/department] to enhance [quality of candidates/time-to-hire/candidate experience]. Focus areas:\n- Job description and requirements refinement\n- Sourcing strategy optimization\n- Interview process and evaluation criteria\n- Onboarding procedure enhancements\n- Success metrics and feedback collection",
    departments: ["Human Resources", "Operations"]
  },
  {
    id: 12,
    value: "sales",
    label: "Sales Strategy",
    template: "Develop sales strategy for [product/service/market segment]. The strategy should address:\n- Target customer profile and segmentation\n- Value proposition and messaging by segment\n- Sales process optimization for [specific stage]\n- Competitive positioning and differentiation\n- Sales enablement tools and resources needed\n- Performance metrics and incentive alignment",
    departments: ["Sales & Business Dev", "Marketing & Growth"]
  }
];

// Expected Output Templates
export const expectedOutputTemplates = [
  { 
    id: 1,
    value: "bug", 
    label: "Bug Fix Output", 
    template: "A fully functioning [feature/module] with the bug resolved. Deliverables include:\n- Fixed code with appropriate test coverage\n- Root cause analysis documentation\n- Updated regression test suite\n- Performance impact assessment\n- User-facing documentation updates if applicable",
    departments: ["Product & Technology", "Data & Analytics"]
  },
  { 
    id: 2,
    value: "feature", 
    label: "New Feature Output", 
    template: "A fully implemented and tested [feature name] that meets all requirements. Deliverables include:\n- Production-ready code with comprehensive error handling\n- Technical documentation with architecture decisions\n- API documentation if applicable\n- Unit and integration tests with minimum 85% coverage\n- User documentation and training materials\n- Performance benchmark results\n- Security assessment report",
    departments: ["Product & Technology", "Operations"]
  },
  { 
    id: 3,
    value: "improvement", 
    label: "Improvement Output", 
    template: "Enhanced [feature/module] with improved [specific aspects]. Deliverables include:\n- Performance metrics showing [target improvement percentage]\n- Before/after comparison documentation\n- Updated technical documentation\n- User impact analysis\n- Rollout and rollback plan\n- Training materials for support team",
    departments: ["Product & Technology", "Operations", "Data & Analytics"]
  },
  { 
    id: 4,
    value: "research", 
    label: "Research Output", 
    template: "A comprehensive report documenting findings about [topic/technology], including:\n- Executive summary with key recommendations\n- Detailed analysis with supporting data\n- Cost-benefit analysis and ROI projections\n- Implementation roadmap with resource requirements\n- Risk assessment matrix\n- Competitive analysis\n- Appendix with technical details and methodology",
    departments: ["Product & Technology", "Marketing & Growth", "Data & Analytics", "Sales & Business Dev"]
  },
  {
    id: 5,
    value: "documentation",
    label: "Documentation Output",
    template: "Complete documentation package including:\n- Executive briefing document\n- Comprehensive user manual with screenshots and examples\n- API/technical reference formatted to our standard template\n- Internal wiki updates\n- Training slide deck\n- FAQ document based on common user questions\n- Video walkthrough of key workflows",
    departments: ["Product & Technology", "Operations", "Customer Success"]
  },
  {
    id: 6,
    value: "marketing",
    label: "Marketing Campaign Output",
    template: "A ready-to-launch marketing campaign package including:\n- Campaign strategy document with target audience analysis\n- Channel-specific content calendar\n- Creative briefs for all required assets\n- Budget allocation spreadsheet\n- Campaign tracking setup documentation\n- Launch checklist and timeline\n- Post-campaign analysis framework",
    departments: ["Marketing & Growth", "Sales & Business Dev"]
  },
  {
    id: 7,
    value: "customer",
    label: "Customer Experience Output",
    template: "Customer experience enhancement deliverables:\n- Customer journey map highlighting improvements\n- Implementation specifications for all touchpoint changes\n- Updated SOP documentation for customer-facing teams\n- Training materials for customer service representatives\n- Metrics dashboard for ongoing monitoring\n- Customer feedback collection mechanism",
    departments: ["Customer Success", "Operations", "Marketing & Growth"]
  },
  {
    id: 8,
    value: "analytics",
    label: "Data Analysis Output",
    template: "Comprehensive data analysis package including:\n- Executive summary with key insights and recommendations\n- Detailed analysis report with methodology\n- Interactive dashboard for ongoing monitoring\n- Raw data files and processing scripts\n- Presentation slide deck for stakeholders\n- Recommended next steps and further areas for investigation",
    departments: ["Data & Analytics", "Finance", "Marketing & Growth", "Sales & Business Dev"]
  },
  {
    id: 9,
    value: "compliance",
    label: "Compliance Update Output",
    template: "Complete compliance implementation package:\n- Updated policy and procedure documentation\n- Gap analysis report with remediation evidence\n- System configuration changes documentation\n- Completed training materials and attendance records\n- Compliance verification test results\n- Ongoing monitoring and reporting framework\n- Third-party audit readiness checklist if applicable",
    departments: ["Legal & Compliance", "Finance", "Human Resources", "Operations"]
  },
  {
    id: 10,
    value: "financial",
    label: "Financial Process Output",
    template: "Optimized financial process deliverables:\n- Process flow documentation (current vs. optimized)\n- Implementation report with metrics comparison\n- Updated financial controls documentation\n- System configuration changes\n- User training materials\n- Audit trail and compliance verification\n- Performance dashboard for ongoing monitoring",
    departments: ["Finance", "Operations", "Legal & Compliance"]
  },
  {
    id: 11,
    value: "hiring",
    label: "Recruitment Process Output",
    template: "Enhanced recruitment process package:\n- Documented process workflow with roles and responsibilities\n- Standardized templates (job descriptions, evaluation forms, etc.)\n- ATS configuration documentation\n- Interview training materials\n- Onboarding checklist and materials\n- Metrics dashboard for recruitment KPIs\n- Candidate and hiring manager feedback mechanisms",
    departments: ["Human Resources", "Operations"]
  },
  {
    id: 12,
    value: "sales",
    label: "Sales Strategy Output",
    template: "Complete sales strategy package including:\n- Go-to-market playbook by customer segment\n- Sales process documentation with stage definitions\n- Competitive battle cards\n- Sales enablement toolkit (pitch decks, scripts, objection handling)\n- CRM setup documentation\n- Performance dashboard with key metrics\n- Training program for sales team\n- Compensation and incentive structure recommendations",
    departments: ["Sales & Business Dev", "Marketing & Growth"]
  }
];